// client/src/coms/views/SettingsView.js
// Personal Settings: categories on the left (a list screen on phones), search across every setting,
// and one page per category. Each control says where it is saved (account, this device, this server) and most
// apply as soon as they change; the few that need saving say so. Server-wide administration is Server Settings,
// a separate area.
//
// Other views open a category with window.__voxPendingSettingsTab (old tab names such as 'Stickers' still work)
// and, for Appearance, window.__voxPendingAppearanceSection; or with the vox-open-settings { tab } event.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../settings/settings.css';
import '../personalisation/personalisationSettings.css';
import { CATEGORIES, SETTINGS_INDEX, anchorForLegacyTab, categoriesFor, categoryForDevice, hiddenCategoryFor, resolveCategory, searchSettings } from '../settings/settingsRegistry';
import { usePersonalisation } from '../personalisation/PersonalisationProvider';
import ThemeSettings from '../personalisation/ThemeSettings';
import LayoutSettings from '../personalisation/LayoutSettings';
import ServerPrefsSettings from '../personalisation/ServerPrefsSettings';
import { TabsSettingsSection } from '../personalisation/PersonalisationSettings';
import { Row, Section } from '../personalisation/settingsControls';
import useServerNavMode, { SERVER_NAV_DROPDOWN, SERVER_NAV_SIDEBAR, writeServerNavMode } from '../components/comslayout_parts/serverNavPrefs';
import useDeviceSettings from '../settings/useDeviceSettings';
import useHotkeySettings from '../settings/useHotkeySettings';
import AccountSettings from '../settings/sections/AccountSettings';
import AudioDeviceSettings from '../settings/sections/AudioDeviceSettings';
import VoiceVideoSettings from '../settings/sections/VoiceVideoSettings';
import SpeakingAnimationSettings from '../settings/sections/SpeakingAnimationSettings';
import NotificationSettings from '../settings/sections/NotificationSettings';
import PrivacySettings from '../settings/sections/PrivacySettings';
import ConnectionSettings from '../settings/sections/ConnectionSettings';
import StickerSoundSettings from '../settings/sections/StickerSoundSettings';
import ShortcutSettings from '../settings/sections/ShortcutSettings';
import AdvancedSettings from '../settings/sections/AdvancedSettings';
import MobileLayoutSettings from '../settings/sections/MobileLayoutSettings';

const LS_LAST_CATEGORY = 'vox:settings:category';
const PERSONALISATION_CATEGORIES = new Set(['layout', 'themes', 'server']);
const TESTIDS = { themes: 'pz-section-theme', layout: 'pz-section-layout', server: 'pz-section-server' };

function takePending() {
  let tab = '';
  let section = '';
  try {
    tab = String(window.__voxPendingSettingsTab || '');
    section = String(window.__voxPendingAppearanceSection || '');
    window.__voxPendingSettingsTab = '';
    window.__voxPendingAppearanceSection = '';
  } catch {}
  return { tab, section };
}

function readLastCategory() {
  try {
    const v = window.localStorage.getItem(LS_LAST_CATEGORY) || '';
    return CATEGORIES.some((c) => c.id === v) ? v : '';
  } catch {
    return '';
  }
}

function useNarrow(maxWidth = 760) {
  const query = `(max-width: ${maxWidth}px)`;
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(query).matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on));
  }, [query]);
  return narrow;
}

function ServerNavSetting() {
  const mode = useServerNavMode();
  return (
    <Row
      label="Server navigation"
      hint="Dropdown opens servers from the button above the channel list. Sidebar shows every server in a column on the left; drag icons, or press Alt+Shift+↑/↓, to reorder. Switching never disconnects voice."
      settingId="server-nav"
      scope="device"
    >
      <div className="vx-seg" role="radiogroup" aria-label="Server navigation">
        {[
          [SERVER_NAV_DROPDOWN, 'Dropdown'],
          [SERVER_NAV_SIDEBAR, 'Sidebar'],
        ].map(([v, label]) => (
          <button key={v} type="button" role="radio" aria-checked={mode === v} className={`vx-seg-btn${mode === v ? ' is-on' : ''}`} onClick={() => writeServerNavMode(v)} data-testid={`server-nav-${v}`}>
            {label}
          </button>
        ))}
      </div>
    </Row>
  );
}

function ThisServerCategory({ serverKey, serverName, channels }) {
  return (
    <>
      <ServerPrefsSettings serverKey={serverKey} serverName={serverName} channels={channels} />
      {serverKey ? (
        <div className="vx-set-callout">
          <div>
            <strong>Looking for settings that affect everyone?</strong>
            <div className="vx-set-hint">Roles, channels, invites, moderation and other server-wide settings are in Server Settings (shown if you have permission to manage something there).</div>
          </div>
          <button type="button" className="vx-btn small" onClick={() => window.dispatchEvent(new CustomEvent('vox-open-server-admin', { detail: { section: 'overview' } }))} data-testid="open-server-settings">
            Open Server Settings
          </button>
        </div>
      ) : null}
    </>
  );
}

export default function SettingsView({ onClose, user = null, serverKey = '', serverName = '', serverChannels = [], onCustomiseLayout }) {
  const pz = usePersonalisation();
  const narrow = useNarrow();
  const initial = useMemo(() => takePending(), []);
  const [category, setCategory] = useState(() => resolveCategory(initial.tab, initial.section) || readLastCategory() || 'account');
  // Phones show the category list first unless another view asked for a particular category.
  const [mobilePane, setMobilePane] = useState(() => (initial.tab ? 'detail' : 'list'));
  const [query, setQuery] = useState('');
  const [flash, setFlash] = useState(initial.tab ? anchorForLegacyTab(initial.tab, initial.section) : '');
  const [navSeq, setNavSeq] = useState(0);
  const contentRef = useRef(null);
  const searchRef = useRef(null);

  const dev = useDeviceSettings();
  const keys = useHotkeySettings();

  const open = useCallback((cat, anchor = '') => {
    setCategory(cat);
    setMobilePane('detail');
    setFlash(anchor);
    setNavSeq((n) => n + 1);
    try {
      window.localStorage.setItem(LS_LAST_CATEGORY, cat);
    } catch {}
  }, []);

  // Requests while Settings is already open (e.g. the sticker picker's "Manage" button).
  useEffect(() => {
    const onOpen = (e) => {
      const tab = e && e.detail && e.detail.tab;
      if (!tab) return;
      const { section } = takePending();
      const cat = resolveCategory(tab, section);
      if (cat) open(cat, anchorForLegacyTab(tab, section));
    };
    window.addEventListener('vox-open-settings', onOpen);
    return () => window.removeEventListener('vox-open-settings', onOpen);
  }, [open]);

  // New category: back to the top, or to the requested setting with a brief highlight.
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return undefined;
    if (!flash) {
      root.scrollTop = 0;
      return undefined;
    }
    let t1 = 0;
    let t2 = 0;
    const find = () => root.querySelector(`[id="vx-setting-${flash}"]`);
    const go = () => {
      const el = find();
      if (!el) return false;
      el.scrollIntoView({ block: 'center', behavior: pz.reduceMotion ? 'auto' : 'smooth' });
      el.classList.add('vx-set-flash');
      const focusable = el.querySelector('button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusable) focusable.focus({ preventScroll: true });
      t2 = setTimeout(() => el.classList.remove('vx-set-flash'), 1800);
      return true;
    };
    // Some sections load their data first; try again briefly.
    if (!go()) t1 = setTimeout(() => go(), 350);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
    // navSeq: choosing the same result again scrolls to it again.
  }, [category, flash, navSeq, pz.reduceMotion]);

  // Phones never show the desktop layout settings, and larger screens never show the mobile ones.
  const isMobileLayout = !!pz.isMobileLayout;
  const cats = useMemo(() => categoriesFor(isMobileLayout), [isMobileLayout]);
  const results = useMemo(() => searchSettings(query).filter((r) => r.cat !== hiddenCategoryFor(isMobileLayout)), [query, isMobileLayout]);
  const shownCategory = categoryForDevice(category, isMobileLayout);
  const current = cats.find((c) => c.id === shownCategory) || cats[0];

  const onSearchKey = (e) => {
    if (e.key === 'Escape') {
      setQuery('');
      return;
    }
    if (e.key === 'Enter' && results[0]) {
      e.preventDefault();
      open(results[0].cat, results[0].id);
      setQuery('');
    }
  };

  const body = (() => {
    switch (current.id) {
      case 'account':
        return <AccountSettings user={user} />;
      case 'voice':
        return <VoiceVideoSettings dev={dev} keys={keys} />;
      case 'audio':
        return <AudioDeviceSettings dev={dev} />;
      case 'animations':
        return <SpeakingAnimationSettings gateDb={dev.gateDb} inputId={dev.inputId} />;
      case 'layout':
        return (
          <>
            <LayoutSettings onCustomiseOnScreen={() => typeof onCustomiseLayout === 'function' && onCustomiseLayout()} />
            <div data-testid="pz-section-tabs">
              <Section id="vx-cat-interface" title="Navigation and tabs">
                <ServerNavSetting />
                <TabsSettingsSection />
              </Section>
            </div>
          </>
        );
      case 'mobile':
        return <MobileLayoutSettings />;
      case 'themes':
        return <ThemeSettings />;
      case 'notifications':
        return <NotificationSettings serverName={serverName} onOpenCategory={open} />;
      case 'privacy':
        return <PrivacySettings />;
      case 'connections':
        return <ConnectionSettings />;
      case 'stickers':
        return <StickerSoundSettings serverKey={serverKey} serverName={serverName} />;
      case 'shortcuts':
        return <ShortcutSettings keys={keys} />;
      case 'server':
        return <ThisServerCategory serverKey={serverKey} serverName={serverName} channels={serverChannels} />;
      case 'advanced':
        return <AdvancedSettings dev={dev} />;
      default:
        return null;
    }
  })();

  const showList = !narrow || mobilePane === 'list';
  const showDetail = !narrow || mobilePane === 'detail';

  return (
    <div className="coms-view vx-settings-view">
      <div className="vx-settings vx-pz" data-testid="personal-settings" data-category={current.id}>
        <header className="vx-settings-head">
          {narrow && mobilePane === 'detail' ? (
            <button type="button" className="vx-btn small vx-settings-back" onClick={() => setMobilePane('list')} aria-label="All settings">
              ‹ Settings
            </button>
          ) : null}
          <div className="vx-settings-title">
            <h1>{narrow && mobilePane === 'detail' ? current.label : 'Personal Settings'}</h1>
            {!narrow || mobilePane === 'list' ? <span className="vx-set-hint">Just for you{user && user.username ? ` (${user.username})` : ''}. Server-wide options are in Server Settings.</span> : null}
          </div>
          <button type="button" className="vx-btn small" onClick={() => typeof onClose === 'function' && onClose()} data-testid="settings-close">
            Done
          </button>
        </header>

        {pz.syncError ? (
          <div className="vx-acct-error vx-settings-sync" role="status">
            {pz.syncError}
          </div>
        ) : null}

        <div className={`vx-settings-body${narrow ? ' is-narrow' : ''}`}>
          {showList ? (
            <nav className="vx-settings-nav" aria-label="Settings categories">
              <div className="vx-settings-search">
                <input
                  ref={searchRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onSearchKey}
                  placeholder="Search settings"
                  aria-label="Search settings"
                  aria-controls="vx-settings-results"
                  data-testid="settings-search"
                />
              </div>
              {query.trim() ? (
                <ul className="vx-settings-results" id="vx-settings-results" aria-label="Search results" data-testid="settings-results">
                  {results.length ? (
                    results.slice(0, 12).map((r) => (
                      <li key={r.id}>
                        <button
                          type="button"
                          onClick={() => {
                            open(r.cat, r.id);
                            setQuery('');
                          }}
                          data-testid={`settings-result-${r.id}`}
                        >
                          <span className="vx-settings-result-label">{r.label}</span>
                          <span className="vx-set-hint">{r.catLabel}</span>
                        </button>
                      </li>
                    ))
                  ) : (
                    <li className="vx-settings-empty" role="status">
                      No settings match “{query.trim()}”. Try a simpler word, such as “microphone” or “theme”.
                    </li>
                  )}
                </ul>
              ) : (
                <ul className="vx-settings-cats">
                  {cats.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        className={`vx-settings-cat${c.id === current.id && (!narrow || mobilePane === 'detail') ? ' is-on' : ''}`}
                        aria-current={c.id === current.id ? 'page' : undefined}
                        onClick={() => open(c.id)}
                        data-testid={TESTIDS[c.id] || `settings-cat-${c.id}`}
                        data-cat={c.id}
                      >
                        <span className="vx-settings-cat-icon" aria-hidden="true">
                          {c.icon}
                        </span>
                        <span className="vx-settings-cat-text">
                          <span>{c.label}</span>
                          {narrow ? <span className="vx-set-hint">{c.description}</span> : null}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {!narrow ? <div className="vx-settings-legend vx-set-hint">{SETTINGS_INDEX.length} settings. Most changes apply straight away; ones that need saving show a Save button.</div> : null}
            </nav>
          ) : null}

          {showDetail ? (
            <main className="vx-settings-content" ref={contentRef} aria-label={current.label} tabIndex={-1}>
              <div className={`vx-set${PERSONALISATION_CATEGORIES.has(current.id) ? ' is-personalisation' : ''}`} data-testid={PERSONALISATION_CATEGORIES.has(current.id) ? 'personalisation-settings' : undefined}>
                {body}
              </div>
            </main>
          ) : null}
        </div>
      </div>
    </div>
  );
}
